import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { isAfterHoursCT, timeUntilOpenCT } from '../lib/payoutHours';
import { useAuth } from '../contexts/AuthContext';

/**
 * PayoutHoursChip — small amber pill in the header that only shows when
 * the payout desk (8 AM – 10 PM CT) is currently closed. Shows the live
 * "opens in Xh YYm" countdown so a player checking their balance at 2 AM
 * sees exactly when they can expect their money to move — no need to
 * open the Cash Out dialog first.
 *
 * Hidden for admin/cashier accounts. Refreshes every 30 s.
 */
const PayoutHoursChip = ({ variant = 'desktop' }) => {
    const { user } = useAuth();
    const [countdown, setCountdown] = useState(() => timeUntilOpenCT());
    const [closed, setClosed] = useState(() => isAfterHoursCT());

    useEffect(() => {
        const tick = () => {
            setClosed(isAfterHoursCT());
            setCountdown(timeUntilOpenCT());
        };
        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, []);

    if (!user || user.role !== 'user') return null;
    if (!closed) return null;

    const base = variant === 'mobile'
        ? 'inline-flex items-center gap-1 px-2 py-1 text-[10px]'
        : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px]';

    return (
        <span
            className={`${base} rounded-full font-bold border border-[#FFB84D]/50 bg-[#FFB84D]/10 text-[#FFB84D] whitespace-nowrap`}
            data-testid="header-payout-hours-chip"
            title="Payout desk is closed. Requests sit in the queue and are worked at 8 AM CT."
        >
            <Clock className={variant === 'mobile' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            <span className="text-[#A0AAB2] font-normal">Payouts open in</span>
            <span className="tabular-nums">{countdown}</span>
        </span>
    );
};

export default PayoutHoursChip;
